import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-all duration-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:focus-visible:ring-offset-dark-primary',
  {
    variants: {
      variant: {
        default: 'bg-primary-500 text-white hover:bg-primary-600 focus-visible:ring-primary-400 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus-visible:ring-primary-300',
        outline: 'border border-primary-200 bg-white text-primary-700 hover:bg-primary-50 focus-visible:ring-primary-400 dark:border-dark-primary dark:bg-dark-card dark:text-primary-300 dark:hover:bg-dark-hover',
        secondary: 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200 focus-visible:ring-secondary-400 dark:bg-dark-secondary dark:text-secondary-300 dark:hover:bg-dark-hover',
        ghost: 'text-primary-600 hover:bg-primary-50 hover:text-primary-700 focus-visible:ring-primary-400 dark:text-primary-300 dark:hover:bg-dark-hover dark:hover:text-primary-200',
        danger: 'bg-error-DEFAULT text-white hover:bg-error-dark focus-visible:ring-error-light dark:bg-error-600 dark:hover:bg-error-700',
        success: 'bg-success-DEFAULT text-white hover:bg-success-dark focus-visible:ring-success-light dark:bg-success-600 dark:hover:bg-success-700',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 px-3 py-1 text-xs',
        lg: 'h-12 px-6 py-3 text-base',
        icon: 'h-10 w-10',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean | undefined;
  icon?: React.ReactNode;
}

export function Button({
  className,
  variant,
  size,
  fullWidth,
  loading,
  icon,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={buttonVariants({ variant, size, fullWidth, className })}
      {...props}
      disabled={loading || props.disabled}
    >
      {loading && (
        <svg
          className="mr-2 h-4 w-4 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      {icon && !loading && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
} 